/**
 * Get Current App State Tool
 *
 * This tool provides the AI with context about the current application state,
 * including navigation, UI preferences, and active view configurations.
 *
 * Usage examples for the AI:
 * - When user asks "What am I looking at?" or "What page am I on?"
 * - For contextual questions like "Is this good?" - check if they're in split builder
 * - To understand user's preferences (theme, units, etc.)
 * - To provide page-specific help or suggestions
 *
 * The AI should use this tool when:
 * 1. User asks vague/contextual questions that need current page context
 * 2. Before providing UI-specific guidance
 * 3. To understand what the user is currently working on
 */

import { z } from 'zod'
import type { AITool } from './types'

// Import stores and utilities
import { DAYS_OF_WEEK } from '../../lib/constants'
import { useSplitConfigStore } from '../../stores/splitConfigStore'
import { useSplitUIStore } from '../../stores/splitUIStore'
import { useUIStore } from '../../stores/uiStore'

interface AppState {
  currentPage: string
  measurementUnit: 'metric' | 'imperial'
  dataSource: 'hevy' | 'csv'
  activeModal?: string // Track which modal is currently open
  context: {
    // Only relevant context for the current page
    [key: string]: unknown
  }
  activeWork?: {
    type: string
    summary: string
    details?: unknown
  }
}

export const getCurrentAppState: AITool<{}, AppState> = {
  name: 'getCurrentAppState',
  description: 'Get current app context. Use silently for contextual responses.',

  parameters: z.object({}),

  execute: async (_, context) => {
    // Get current route from window location
    const currentPath = window.location.pathname
    const searchParams = new URLSearchParams(window.location.search)

    // Get active modal and its data from UI store
    const { activeModal, activeModalData } = useUIStore.getState()

    // Determine page name
    let pageName = 'dashboard'
    if (currentPath === '/' || currentPath === '') pageName = 'dashboard'
    else if (currentPath.includes('/workouts')) pageName = 'workouts'
    else if (currentPath.includes('/routines')) pageName = 'routines'
    else if (currentPath.includes('/split')) pageName = 'split-builder'
    else if (currentPath.includes('/settings')) pageName = 'settings'
    else if (currentPath.includes('/coach')) pageName = 'coach'

    // Build minimal context based on current page
    let pageContext: Record<string, unknown> = {}
    let activeWork: AppState['activeWork'] = undefined

    if (pageName === 'split-builder') {
      const splitConfig = useSplitConfigStore.getState()
      const splitUI = useSplitUIStore.getState()

      // Build detailed split context
      // Create day-by-day breakdown
      const weeklySchedule = DAYS_OF_WEEK.map((day) => {
        const routineId = splitConfig.dayAssignments[day]
        const routine = routineId ? splitConfig.routines[routineId] : null

        if (!routine) {
          return {
            day,
            rest: true
          }
        }

        return {
          day,
          routine: routine.name,
          exercises: routine.exercises.map((ex) => ({
            name: ex.name,
            sets: ex.sets.length,
            primaryMover: ex.primaryMover,
            secondaryMovers: ex.secondaryMovers,
            details: ex.sets.map((set) => ({
              reps: set.reps,
              weight: set.weight ? `${set.weight}kg` : 'bodyweight'
            }))
          }))
        }
      })

      // Volume breakdown by muscle group
      const volumeByMuscle = {
        primaryOnly: Object.entries(splitConfig.weeklyVolumePrimaryOnly)
          .filter(([_, sets]) => sets > 0)
          .sort(([_, a], [__, b]) => b - a)
          .map(([muscle, sets]) => ({ muscle, sets })),
        withSecondary: Object.entries(splitConfig.weeklyVolume)
          .filter(([_, sets]) => sets > 0)
          .sort(([_, a], [__, b]) => b - a)
          .map(([muscle, sets]) => ({ muscle, sets }))
      }

      pageContext = {
        splitName: splitConfig.splitName || 'Untitled Split',
        totalSets: splitConfig.totalSets,
        selectedDay: splitUI.selectedDay,
        routineCount: Object.keys(splitConfig.routines).length,
        activeDays: Object.values(splitConfig.dayAssignments).filter((id) => id !== null).length,
        weeklySchedule,
        volumeByMuscle,
        routines: Object.entries(splitConfig.routines).map(([id, routine]) => ({
          id,
          name: routine.name,
          exerciseCount: routine.exercises.length,
          totalSets: routine.exercises.reduce((sum, ex) => sum + ex.sets.length, 0)
        }))
      }

      activeWork = {
        type: 'split_configuration',
        summary: `Configuring "${splitConfig.splitName || 'Untitled Split'}" - ${splitConfig.totalSets} weekly sets across ${Object.values(splitConfig.dayAssignments).filter((id) => id !== null).length} training days`,
        details: pageContext
      }
    } else if (pageName === 'workouts') {
      const workoutId = currentPath.split('/workout/')[1]
      if (workoutId) {
        pageContext.viewingWorkout = workoutId
      }
    } else if (pageName === 'settings') {
      pageContext.tab = searchParams.get('tab') || 'general'
    }

    // Add modal-specific context with full data
    if (activeModal) {
      pageContext.activeModal = activeModal

      // Add modal-specific information and full data based on modal type
      switch (activeModal) {
        case 'workoutDetail':
          if (activeModalData?.workout) {
            const workout = activeModalData.workout
            const stats = activeModalData.stats
            const muscleGroups = activeModalData.muscleGroupData

            pageContext.modalContext = {
              type: 'workout_details',
              description: 'User is viewing detailed workout information',
              workoutDetails: {
                // Basic workout info
                title: workout.title,
                date: workout.start_time,
                duration: stats?.duration,

                // Workout Statistics (matching the UI card)
                statistics: {
                  exercises: stats?.exerciseCount || 0,
                  totalSets: stats?.totalSets || 0,
                  totalVolume: stats?.totalVolume || 0,
                  avgRpe: stats?.avgRpe || null,
                  heaviestWeight: stats?.heaviestWeight || 0,
                  minReps: stats?.minReps || 0,
                  totalReps: stats?.totalReps || 0,
                  highestReps: stats?.highestReps || 0,
                  avgRepsPerSet: stats?.avgRepsPerSet || 0,
                  avgVolumePerSet: stats?.avgVolumePerSet || 0
                },

                // Volume Distribution (muscle groups)
                volumeDistribution:
                  muscleGroups?.map((mg: any) => ({
                    muscleGroup: mg.muscleGroup,
                    displayName: mg.muscleGroup, // Could map to display names
                    sets: mg.sets,
                    percentage: mg.percentage || 0
                  })) || [],

                // Volume by muscle group (actual weight moved)
                muscleGroupVolume: activeModalData.muscleGroupVolumeData || [],

                // Detailed exercises array
                exercises:
                  workout.exercises?.map((exercise: any) => ({
                    name: exercise.title || 'Unknown Exercise',
                    primaryMover: exercise.primaryMover || 'Unknown',
                    secondaryMovers: exercise.secondaryMovers || [],
                    exerciseTemplateId: exercise.exercise_template_id,
                    sets:
                      exercise.sets?.map((set: any, setIndex: number) => ({
                        setNumber: setIndex + 1,
                        weight: set.weight_kg || 0,
                        reps: set.reps || 0,
                        rpe: set.rpe || null,
                        type: set.type || 'working', // working, warmup, dropset, etc.
                        distance: set.distance_meters || null,
                        duration: set.duration_seconds || null,
                        notes: set.notes || null,
                        // Calculated values
                        volume: (set.reps || 0) * (set.weight_kg || 0)
                      })) || [],
                    // Exercise totals
                    totalSets: exercise.sets?.length || 0,
                    totalVolume:
                      exercise.sets?.reduce(
                        (sum: number, set: any) => sum + (set.reps || 0) * (set.weight_kg || 0),
                        0
                      ) || 0,
                    maxWeight: Math.max(
                      ...(exercise.sets?.map((set: any) => set.weight_kg || 0) || [0])
                    ),
                    totalReps:
                      exercise.sets?.reduce((sum: number, set: any) => sum + (set.reps || 0), 0) ||
                      0,
                    avgRpe:
                      exercise.sets
                        ?.filter((s: any) => s.rpe)
                        .reduce(
                          (sum: number, set: any, _: any, arr: any[]) => sum + set.rpe / arr.length,
                          0
                        ) || null
                  })) || [],

                // Settings
                includeSecondaryMovers: activeModalData.includeSecondaryMovers
              }
            }
          } else {
            // Modal is open but no data - provide basic info
            pageContext.modalContext = {
              type: 'workout_details',
              description: 'User is viewing workout details but data is not available',
              error: 'Modal data not captured'
            }
          }
          break
        case 'exerciseSelection':
          pageContext.modalContext = {
            type: 'exercise_selection',
            description: 'User is selecting exercises to add to their routine in the split builder',
            data: activeModalData || 'Modal data not available'
          }
          break
        case 'routineSelection':
          pageContext.modalContext = {
            type: 'routine_selection',
            description: 'User is selecting or managing routines',
            data: activeModalData || 'Modal data not available'
          }
          break
        case 'muscleGroupOverride':
          pageContext.modalContext = {
            type: 'muscle_group_configuration',
            description: 'User is customizing muscle group targets for specific exercises',
            data: activeModalData || 'Modal data not available'
          }
          break
      }
    }

    // Check data source
    const hasHevyCredentials =
      !!context.userCredentials?.apiKey && context.userCredentials.apiKey !== 'CSV_IMPORT'

    const appState: AppState = {
      currentPage: pageName,
      measurementUnit: 'metric', // Default for now, could check user settings
      dataSource: hasHevyCredentials ? 'hevy' : 'csv',
      activeModal: activeModal || undefined,
      context: pageContext,
      activeWork
    }

    return appState
  }
}
